import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api/api';
import './LookupField.css';

const LookupField = ({ 
    field, 
    value, 
    onChange, 
    onMultiFieldChange,
    placeholder = 'Select...', 
    disabled = false, 
    className = 'form-input' 
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Extract lookup configuration from field
    const lookupTable = field.lookupTable;
    const lookupValueColumn = field.lookupValueColumn;
    const lookupDisplayColumn = field.lookupDisplayColumn;
    const lookupReturnColumn = field.lookupReturnColumn;

    useEffect(() => {
        const loadLookupData = async () => {
            if (!lookupTable || !lookupValueColumn || !lookupDisplayColumn) {
                console.warn('🔍 LookupField: Missing lookup configuration', { 
                    field,
                    lookupTable,
                    lookupValueColumn,
                    lookupDisplayColumn,
                    lookupReturnColumn
                });
                return;
            }

            // Check if this is a multi-column display configuration
            const displayColumns = lookupDisplayColumn.split(',').map(col => col.trim()).filter(col => col);
            const isMultiColumn = displayColumns.length > 1;

            // Check if this is a multi-column return configuration
            const returnColumns = lookupReturnColumn ? lookupReturnColumn.split(',').map(col => col.trim()).filter(col => col) : [];
            const isMultiReturn = returnColumns.length > 0;

            console.log('🔍 LookupField: Starting data load for field:', {
                fieldName: field.name || field.dbColumn,
                lookupTable,
                lookupValueColumn,
                lookupDisplayColumn,
                lookupReturnColumn,
                displayColumns,
                returnColumns,
                isMultiColumn,
                isMultiReturn,
                fullField: field
            });

            setLoading(true);
            setError(null);

            try {
                // Build API URL with proper display column and return column parameters
                let apiUrl = `/api/db/lookup/${lookupTable}?valueColumn=${lookupValueColumn}&displayColumn=${encodeURIComponent(lookupDisplayColumn)}`;
                if (lookupReturnColumn) {
                    apiUrl += `&returnColumn=${encodeURIComponent(lookupReturnColumn)}`;
                }
                
                console.log('🔍 LookupField: Calling API:', apiUrl);

                const response = await apiFetch(apiUrl);
                
                console.log('🔍 LookupField: Raw API response:', response);
                
                if (Array.isArray(response)) {
                    setOptions(response);
                    console.log('✅ LookupField: Lookup data loaded successfully:', {
                        count: response.length,
                        isMultiColumn,
                        isMultiReturn,
                        displayColumns,
                        returnColumns,
                        sampleOptions: response.slice(0, 3),
                        options: response
                    });
                } else {
                    throw new Error('Invalid response format - expected array');
                }
            } catch (err) {
                console.error('❌ LookupField: Failed to load lookup data:', {
                    error: err,
                    message: err.message,
                    field: field.name || field.dbColumn,
                    lookupTable,
                    lookupValueColumn,
                    lookupDisplayColumn,
                    lookupReturnColumn,
                    displayColumns,
                    returnColumns
                });
                setError(err.message || 'Failed to load options');
                setOptions([]);
            } finally {
                setLoading(false);
            }
        };

        loadLookupData();
    }, [lookupTable, lookupValueColumn, lookupDisplayColumn, lookupReturnColumn]);

    const handleChange = (e) => {
        const selectedValue = e.target.value;
        const selectedOption = options.find(opt => opt.value === selectedValue);
        
        console.log('🔄 Lookup field value changed:', {
            field: field.name || field.dbColumn,
            value: selectedValue,
            selectedOption,
            hasReturnData: selectedOption?.returnData ? 'Yes' : 'No'
        });

        // Call the standard onChange for the primary field
        onChange(selectedValue);

        // If we have multi-field return data and a callback, populate other fields
        if (selectedOption?.returnData && onMultiFieldChange && lookupReturnColumn) {
            const returnColumns = lookupReturnColumn.split(',').map(col => col.trim()).filter(col => col);
            const fieldMappings = {};
            
            // Create field mappings from return data
            returnColumns.forEach(columnName => {
                if (selectedOption.returnData[columnName] !== undefined) {
                    fieldMappings[columnName] = selectedOption.returnData[columnName];
                }
            });

            console.log('🔄 Multi-field population:', {
                returnColumns,
                returnData: selectedOption.returnData,
                fieldMappings
            });

            // Call the multi-field change callback
            onMultiFieldChange(fieldMappings);
        }
    };

    // Find the display value for the current value
    const getDisplayValue = () => {
        if (!value || options.length === 0) return '';
        const option = options.find(opt => opt.value === value || opt.value == value);
        return option ? option.display : value;
    };

    if (error) {
        return (
            <div className="lookup-field-container">
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    className={`lookup-field lookup-error ${className}`}
                    placeholder={placeholder}
                    disabled={disabled}
                />
                <div className="lookup-error-message">
                    Error loading options: {error}
                </div>
            </div>
        );
    }

    return (
        <div className="lookup-field-container">
            <select
                value={value || ''}
                onChange={handleChange}
                className={`lookup-field ${loading ? 'loading' : ''} ${className}`}
                disabled={disabled || loading}
            >
                <option value="">
                    {loading ? 'Loading...' : placeholder}
                </option>
                {options.map((option, index) => (
                    <option key={index} value={option.value} title={option.display}>
                        {option.display}
                    </option>
                ))}
            </select>
            
            {loading && <div className="lookup-loading-spinner" />}
            
            {/* Debug info in development */}
            {process.env.NODE_ENV === 'development' && (
                <div className="lookup-debug-info">
                    Table: {lookupTable} | Value: {lookupValueColumn} | Display: {lookupDisplayColumn} | Options: {options.length}
                    {lookupDisplayColumn && lookupDisplayColumn.includes(',') && (
                        <div style={{fontSize: '11px', color: '#666', marginTop: '2px'}}>
                            📊 Multi-column display: {lookupDisplayColumn.split(',').length} columns
                        </div>
                    )}
                    {lookupReturnColumn && (
                        <div style={{fontSize: '11px', color: '#666', marginTop: '2px'}}>
                            🔄 Multi-field return: {lookupReturnColumn.split(',').length} columns ({lookupReturnColumn})
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LookupField; 